import React, { useState } from "react";
import PrivacyModal from "@/components/PrivacyModal";
import Logo from "@/components/Logo";

const SIDES = [
  { id: "south", label: "South", hint: "dance floor (mirrors your moves)" },
  { id: "north", label: "North", hint: "door ahead" },
  { id: "east", label: "East", hint: "window ahead" },
  { id: "west", label: "West", hint: "record player ahead" },
];

const SIDE_META = {
  south: {
    label: "South",
    heading: "Facing North // Dance Floor",
    ahead: "Dance Screen (Mirrors your body)",
    left: "Window (Skylight)",
    right: "Record Player (Turntable)",
    yawDeg: "0°",
    target: "North Wall",
    radarPos: { cx: 40, cy: 62 },
    radarCone: "M 40 62 L 20 20 L 60 20 Z",
  },
  north: {
    label: "North",
    heading: "Facing South // Entrance",
    ahead: "Entrance Door & Serif Sign",
    left: "Record Player (Turntable)",
    right: "Window (Skylight)",
    yawDeg: "180°",
    target: "South Wall",
    radarPos: { cx: 40, cy: 18 },
    radarCone: "M 40 18 L 20 60 L 60 60 Z",
  },
  east: {
    label: "East",
    heading: "Facing West // City Skylight",
    ahead: "Procedural Window (City)",
    left: "Entrance Door",
    right: "Dance Screen (Hologram)",
    yawDeg: "-90°",
    target: "West Wall",
    radarPos: { cx: 62, cy: 40 },
    radarCone: "M 62 40 L 20 20 L 20 60 Z",
  },
  west: {
    label: "West",
    heading: "Facing East // Vinyl Station",
    ahead: "Vinyl Turntable & Poster",
    left: "Dance Screen (Hologram)",
    right: "Entrance Door",
    yawDeg: "+90°",
    target: "East Wall",
    radarPos: { cx: 18, cy: 40 },
    radarCone: "M 18 40 L 60 20 L 60 60 Z",
  },
};

/** Mini-map radar showing room boundaries and camera vantage position */
function RoomRadar({ side }) {
  const meta = SIDE_META[side] || SIDE_META.south;

  return (
    <div
      style={{
        position: "absolute",
        top: "clamp(16px, 2.5vh, 32px)",
        right: "clamp(16px, 2.5vw, 28px)",
        background: "rgba(10, 10, 15, 0.85)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        backdropFilter: "blur(8px)",
        borderRadius: 8,
        padding: "8px 10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        zIndex: 5,
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%", fontSize: 9, letterSpacing: "0.14em", color: "var(--paper)", opacity: 0.8 }} className="font-mono">
        <span>VANTAGE</span>
        <span style={{ color: "var(--neon-cyan)", fontWeight: 700 }}>{meta.yawDeg}</span>
      </div>

      <svg width="80" height="80" viewBox="0 0 80 80" style={{ display: "block" }}>
        {/* Room boundary */}
        <rect x="10" y="10" width="60" height="60" fill="#12121c" stroke="rgba(255,255,255,0.18)" strokeWidth="1.2" rx="2" />

        {/* North Wall: Dance Screen (Cyan accent) */}
        <line x1="22" y1="10" x2="58" y2="10" stroke="var(--neon-cyan)" strokeWidth="2.5" strokeLinecap="round" />
        <text x="40" y="7" fill="var(--neon-cyan)" fontSize="6" fontFamily="var(--font-mono)" textAnchor="middle">N // HOLO</text>

        {/* South Wall: Door (Magenta accent) */}
        <line x1="28" y1="70" x2="52" y2="70" stroke="var(--neon-magenta)" strokeWidth="2.5" strokeLinecap="round" />
        <text x="40" y="77" fill="var(--neon-magenta)" fontSize="6" fontFamily="var(--font-mono)" textAnchor="middle">S // DOOR</text>

        {/* West Wall: Window (Sky blue accent) */}
        <line x1="10" y1="26" x2="10" y2="54" stroke="#7dd3fc" strokeWidth="2.5" strokeLinecap="round" />
        <text x="6" y="42" fill="#7dd3fc" fontSize="5" fontFamily="var(--font-mono)" textAnchor="middle" transform="rotate(-90 6 42)">W // WIN</text>

        {/* East Wall: Record Player (Lime / Purple accent) */}
        <line x1="70" y1="26" x2="70" y2="54" stroke="var(--neon-lime)" strokeWidth="2.5" strokeLinecap="round" />
        <text x="75" y="42" fill="var(--neon-lime)" fontSize="5" fontFamily="var(--font-mono)" textAnchor="middle" transform="rotate(90 75 42)">E // VINYL</text>

        {/* Center room crosshair */}
        <line x1="37" y1="40" x2="43" y2="40" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
        <line x1="40" y1="37" x2="40" y2="43" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />

        {/* View cone radiating from standing point */}
        <path d={meta.radarCone} fill="url(#radarConeG)" opacity="0.35" />

        {/* Standing position dot */}
        <circle cx={meta.radarPos.cx} cy={meta.radarPos.cy} r="4.5" fill="var(--neon-cyan)" filter="drop-shadow(0 0 4px var(--neon-cyan))" />
        <circle cx={meta.radarPos.cx} cy={meta.radarPos.cy} r="2" fill="#fff" />
      </svg>
    </div>
  );
}

/** Perspective room view customized per standing side */
function RoomPreviewSVG({ side }) {
  return (
    <svg
      key={side}
      viewBox="0 0 400 500"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        animation: "previewFade 350ms cubic-bezier(0.16, 1, 0.3, 1) both",
      }}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="floorG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1a1a25" />
          <stop offset="1" stopColor="#0a0a0f" />
        </linearGradient>

        <linearGradient id="windowSkyG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0a192c" />
          <stop offset="45%" stopColor="#1e4460" />
          <stop offset="80%" stopColor="#4f8b9e" />
          <stop offset="100%" stopColor="#d9f2ff" />
        </linearGradient>

        <linearGradient id="radarConeG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00d3d9" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#00d3d9" stopOpacity="0.05" />
        </linearGradient>

        <filter id="glowMagenta" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#ff2e88" floodOpacity="0.9" />
        </filter>

        <filter id="glowCyan" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor="#00d3d9" floodOpacity="0.8" />
        </filter>
      </defs>

      {/* BASE ARCHITECTURE */}
      {/* Far back wall */}
      <polygon points="120,140 280,140 280,340 120,340" fill="#181824" stroke="#2a2a35" strokeWidth="1" />

      {/* Left perspective wall */}
      <polygon points="0,60 120,140 120,340 0,440" fill="#13131c" />

      {/* Right perspective wall */}
      <polygon points="400,60 280,140 280,340 400,440" fill="#13131c" />

      {/* Floor */}
      <polygon points="0,440 120,340 280,340 400,440" fill="url(#floorG)" />

      {/* Ceiling */}
      <polygon points="0,60 120,140 280,140 400,60" fill="#0f0f18" />

      {/* Floor perspective grid lines */}
      <line x1="0" y1="440" x2="120" y2="340" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      <line x1="400" y1="440" x2="280" y2="340" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      <line x1="70" y1="440" x2="150" y2="340" stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="3,3" />
      <line x1="140" y1="440" x2="180" y2="340" stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="3,3" />
      <line x1="260" y1="440" x2="220" y2="340" stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="3,3" />
      <line x1="330" y1="440" x2="250" y2="340" stroke="rgba(255,255,255,0.04)" strokeWidth="1" strokeDasharray="3,3" />
      <line x1="35" y1="410" x2="365" y2="410" stroke="rgba(255,255,255,0.03)" strokeWidth="1" />
      <line x1="70" y1="380" x2="330" y2="380" stroke="rgba(255,255,255,0.03)" strokeWidth="1" />
      <line x1="100" y1="355" x2="300" y2="355" stroke="rgba(255,255,255,0.03)" strokeWidth="1" />

      {/* Ceiling lighting strip */}
      {side === "south" || side === "north" ? (
        // Ceiling strip parallel to far wall (running X direction)
        <g>
          <rect x="145" y="136" width="110" height="4" rx="2" fill="var(--neon-cyan)" opacity="0.95" filter="url(#glowCyan)">
            <animate attributeName="opacity" values="0.75;1;0.75" dur="3s" repeatCount="indefinite" />
          </rect>
          <ellipse cx="200" cy="142" rx="60" ry="10" fill="var(--neon-cyan)" opacity="0.08" />
        </g>
      ) : (
        // Ceiling strip running Z direction (towards vanishing point)
        <g>
          <polygon points="198,70 202,70 201,140 199,140" fill="var(--neon-cyan)" opacity="0.95" filter="url(#glowCyan)">
            <animate attributeName="opacity" values="0.75;1;0.75" dur="3s" repeatCount="indefinite" />
          </polygon>
          <ellipse cx="200" cy="110" rx="25" ry="35" fill="var(--neon-cyan)" opacity="0.08" />
        </g>
      )}

      {/* ==================== VANTAGE-SPECIFIC WALL CONTENTS ==================== */}

      {/* --- 1. SOUTH VANTAGE POINT (Facing North Wall: Dance Screen) --- */}
      {side === "south" && (
        <g>
          {/* Left Wall: Window */}
          <polygon points="18,140 98,188 98,290 18,340" fill="url(#windowSkyG)" opacity="0.9" />
          <line x1="58" y1="164" x2="58" y2="315" stroke="#0a0a0f" strokeWidth="2.5" />
          <line x1="18" y1="240" x2="98" y2="240" stroke="#0a0a0f" strokeWidth="2" />
          {/* Plant on sill */}
          <polygon points="12,342 98,292 98,298 12,348" fill="#141420" />
          <polygon points="76,302 85,298 84,290 76,293" fill="var(--neon-lime)" />
          <circle cx="80" cy="287" r="4" fill="#2e6a3a" />

          {/* Right Wall: Turntable / Record Player */}
          <polygon points="310,274 382,294 382,302 310,282" fill="#151522" />
          <polygon points="316,268 374,284 374,292 316,276" fill="#efefe8" />
          <ellipse cx="344" cy="274" rx="26" ry="7" fill="#0a0a0f" stroke="#252535" strokeWidth="1" />
          <ellipse cx="344" cy="274" rx="9" ry="2.5" fill="var(--neon-magenta)" />
          <circle cx="344" cy="274" r="1.2" fill="#fff" />
          <line x1="364" y1="277" x2="346" y2="273" stroke="#2a2a35" strokeWidth="1.8" />
          {/* Poster above */}
          <polygon points="325,185 365,198 365,246 325,233" fill="#10101a" stroke="rgba(255,46,136,0.3)" strokeWidth="1" />
          <line x1="332" y1="210" x2="358" y2="218" stroke="var(--neon-magenta)" strokeWidth="1.5" />

          {/* Center Wall: Stick-Figure Dance Screen */}
          <rect x="135" y="165" width="130" height="96" rx="4" fill="#0c0c16" stroke="rgba(0,211,217,0.35)" strokeWidth="1.5" />
          <rect x="138" y="168" width="124" height="90" rx="3" fill="#00d3d9" fillOpacity="0.08" />
          <text x="144" y="180" fill="var(--neon-cyan)" fontFamily="var(--font-mono)" fontSize="8" letterSpacing="0.12em">// LIVE</text>
          <circle cx="178" cy="177" r="2.5" fill="var(--neon-lime)" />
          <text x="256" y="180" fill="var(--neon-magenta)" fontFamily="var(--font-mono)" fontSize="6.5" letterSpacing="0.1em" textAnchor="end">BODY MIRROR</text>

          {/* Stick Figure Avatar */}
          <circle cx="200" cy="192" r="8" fill="none" stroke="var(--neon-magenta)" strokeWidth="2.2" />
          <line x1="200" y1="200" x2="200" y2="226" stroke="var(--neon-magenta)" strokeWidth="2.2" strokeLinecap="round" />
          {/* Dancing arms */}
          <path d="M 200 206 L 186 198 L 180 210" fill="none" stroke="var(--neon-cyan)" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M 200 206 L 214 212 L 222 200" fill="none" stroke="var(--neon-cyan)" strokeWidth="2.2" strokeLinecap="round" />
          <circle cx="180" cy="210" r="2.5" fill="var(--neon-cyan)" />
          <circle cx="222" cy="200" r="2.5" fill="var(--neon-cyan)" />
          {/* Dancing legs */}
          <path d="M 200 226 L 188 240 L 184 252" fill="none" stroke="var(--neon-magenta)" strokeWidth="2.2" strokeLinecap="round" />
          <path d="M 200 226 L 212 240 L 218 252" fill="none" stroke="var(--neon-magenta)" strokeWidth="2.2" strokeLinecap="round" />

          {/* Scanning beam */}
          <rect x="138" y="196" width="124" height="2" fill="var(--neon-cyan)" opacity="0.65">
            <animate attributeName="y" values="170;250;170" dur="4s" repeatCount="indefinite" />
          </rect>

          {/* Neon underline */}
          <rect x="135" y="268" width="130" height="3" rx="1.5" fill="var(--neon-magenta)" filter="url(#glowMagenta)" />
          <text x="200" y="284" fill="var(--neon-cyan)" fontFamily="var(--font-mono)" fontSize="7" letterSpacing="0.16em" textAnchor="middle">
            HOLOGRAM DANCE FLOOR
          </text>
        </g>
      )}

      {/* --- 2. NORTH VANTAGE POINT (Facing South Wall: Entrance Door) --- */}
      {side === "north" && (
        <g>
          {/* Left Wall: Turntable / Record Player */}
          <polygon points="90,274 18,294 18,302 90,282" fill="#151522" />
          <polygon points="84,268 26,284 26,292 84,276" fill="#efefe8" />
          <ellipse cx="56" cy="274" rx="26" ry="7" fill="#0a0a0f" stroke="#252535" strokeWidth="1" />
          <ellipse cx="56" cy="274" rx="9" ry="2.5" fill="var(--neon-magenta)" />
          <circle cx="56" cy="274" r="1.2" fill="#fff" />
          <line x1="36" y1="277" x2="54" y2="273" stroke="#2a2a35" strokeWidth="1.8" />
          {/* Poster above */}
          <polygon points="75,185 35,198 35,246 75,233" fill="#10101a" stroke="rgba(255,46,136,0.3)" strokeWidth="1" />
          <line x1="68" y1="210" x2="42" y2="218" stroke="var(--neon-magenta)" strokeWidth="1.5" />

          {/* Right Wall: Window */}
          <polygon points="382,140 302,188 302,290 382,340" fill="url(#windowSkyG)" opacity="0.9" />
          <line x1="342" y1="164" x2="342" y2="315" stroke="#0a0a0f" strokeWidth="2.5" />
          <line x1="382" y1="240" x2="302" y2="240" stroke="#0a0a0f" strokeWidth="2" />
          {/* Plant on sill */}
          <polygon points="388,342 302,292 302,298 388,348" fill="#141420" />
          <polygon points="324,302 315,298 316,290 324,293" fill="var(--neon-lime)" />
          <circle cx="320" cy="287" r="4" fill="#2e6a3a" />

          {/* Center Wall: Door & Serif Sign */}
          {/* Sign above door */}
          <text x="200" y="165" fill="var(--paper)" fontFamily="var(--font-serif)" fontSize="20" letterSpacing="0.08em" textAnchor="middle">
            LOOK AROUND
          </text>
          <text x="200" y="177" fill="var(--neon-cyan)" fontFamily="var(--font-mono)" fontSize="7" letterSpacing="0.2em" textAnchor="middle">
            // DARD-E-DISCO
          </text>

          {/* Neon strip above door */}
          <rect x="156" y="193" width="88" height="3" rx="1.5" fill="var(--neon-magenta)" filter="url(#glowMagenta)" />

          {/* Door frame */}
          <rect x="154" y="200" width="92" height="140" fill="#0d0d16" stroke="#252538" strokeWidth="1.5" />
          {/* Door panel */}
          <rect x="158" y="204" width="84" height="136" fill="#151522" />
          {/* Door seam */}
          <line x1="210" y1="204" x2="210" y2="340" stroke="#242436" strokeWidth="1.5" />

          {/* Door handle (Cyan) */}
          <rect x="168" y="262" width="4" height="22" rx="2" fill="var(--neon-cyan)" filter="url(#glowCyan)" />

          {/* Keypad panel beside door */}
          <rect x="142" y="258" width="8" height="16" rx="1" fill="#0a0a0f" stroke="#333" strokeWidth="0.8" />
          <circle cx="146" cy="263" r="1.8" fill="var(--neon-lime)" filter="drop-shadow(0 0 3px var(--neon-lime))" />
          <line x1="144" y1="267" x2="148" y2="267" stroke="#444" strokeWidth="0.8" />
          <line x1="144" y1="270" x2="148" y2="270" stroke="#444" strokeWidth="0.8" />
        </g>
      )}

      {/* --- 3. EAST VANTAGE POINT (Facing West Wall: Architectural Window) --- */}
      {side === "east" && (
        <g>
          {/* Left Wall: Entrance Door */}
          <polygon points="24,145 96,190 96,335 24,370" fill="#0d0d16" stroke="#2a2a35" strokeWidth="1.2" />
          <polygon points="28,150 92,193 92,330 28,365" fill="#151522" />
          <polygon points="24,140 96,185 96,188 24,143" fill="var(--neon-magenta)" />
          <line x1="60" y1="172" x2="60" y2="347" stroke="#28283c" strokeWidth="1.2" />
          <line x1="78" y1="258" x2="78" y2="276" stroke="var(--neon-cyan)" strokeWidth="2.5" strokeLinecap="round" />
          <polygon points="82,238 91,244 91,256 82,250" fill="#0a0a0f" />
          <circle cx="86" cy="245" r="1.5" fill="var(--neon-lime)" />

          {/* Right Wall: Dance Screen */}
          <polygon points="380,165 304,200 304,290 380,325" fill="#0c0c16" stroke="rgba(0,211,217,0.3)" strokeWidth="1.2" />
          <polygon points="377,170 307,203 307,286 377,320" fill="var(--neon-cyan)" fillOpacity="0.1" />
          <polygon points="380,328 304,293 304,296 380,331" fill="var(--neon-magenta)" />
          <circle cx="342" cy="225" r="4.5" stroke="var(--neon-magenta)" strokeWidth="1.8" fill="none" />
          <path d="M 348 230 L 335 242 L 348 270" stroke="var(--neon-magenta)" strokeWidth="1.8" fill="none" />
          <text x="350" y="184" fill="var(--neon-cyan)" fontFamily="var(--font-mono)" fontSize="7">// LIVE</text>

          {/* Center Wall: Large Panoramic Window with City Skyline */}
          <text x="200" y="152" fill="var(--neon-cyan)" fontFamily="var(--font-mono)" fontSize="8" letterSpacing="0.14em" textAnchor="middle">
            // PROCEDURAL SKYLIGHT
          </text>
          {/* Window frame */}
          <rect x="135" y="160" width="130" height="114" rx="3" fill="#0a0a12" stroke="#252535" strokeWidth="2" />
          {/* Glass & Skyline */}
          <rect x="138" y="163" width="124" height="108" rx="2" fill="url(#windowSkyG)" />
          {/* Distant Skyscrapers silhouette */}
          <path
            d="M 138 245 L 148 245 L 148 210 L 162 210 L 162 245 L 175 245 L 175 195 L 190 195 L 190 245 L 205 245 L 205 215 L 220 215 L 220 245 L 235 245 L 235 200 L 250 200 L 250 245 L 262 245 L 262 271 L 138 271 Z"
            fill="#080c14"
          />
          {/* Tiny glowing window dots */}
          <rect x="152" y="218" width="1.5" height="1.5" fill="#fffae0" />
          <rect x="156" y="224" width="1.5" height="1.5" fill="#fffae0" />
          <rect x="180" y="202" width="1.5" height="1.5" fill="var(--neon-cyan)" />
          <rect x="184" y="214" width="1.5" height="1.5" fill="#fffae0" />
          <rect x="210" y="222" width="1.5" height="1.5" fill="#fffae0" />
          <rect x="240" y="208" width="1.5" height="1.5" fill="var(--neon-cyan)" />

          {/* Window Mullions (Dark Cross) */}
          <rect x="198" y="160" width="4" height="114" fill="#0a0a12" />
          <rect x="135" y="215" width="130" height="4" fill="#0a0a12" />

          {/* Window Sill Shelf */}
          <rect x="130" y="274" width="140" height="7" rx="1" fill="#141420" stroke="#222230" strokeWidth="1" />

          {/* Potted Plant on Sill */}
          <polygon points="228,274 240,274 238,262 230,262" fill="var(--neon-lime)" />
          <circle cx="232" cy="256" r="5" fill="#2e6a3a" />
          <circle cx="237" cy="253" r="6" fill="#388e4a" />
          <circle cx="242" cy="257" r="4.5" fill="#245a30" />

          {/* Ambient window light spill onto floor */}
          <polygon points="135,281 265,281 295,340 105,340" fill="url(#radarConeG)" opacity="0.12" />
        </g>
      )}

      {/* --- 4. WEST VANTAGE POINT (Facing East Wall: Vinyl Station) --- */}
      {side === "west" && (
        <g>
          {/* Left Wall: Dance Screen */}
          <polygon points="20,165 96,200 96,290 20,325" fill="#0c0c16" stroke="rgba(0,211,217,0.3)" strokeWidth="1.2" />
          <polygon points="23,170 93,203 93,286 23,320" fill="var(--neon-cyan)" fillOpacity="0.1" />
          <polygon points="20,328 96,293 96,296 20,331" fill="var(--neon-magenta)" />
          <circle cx="58" cy="225" r="4.5" stroke="var(--neon-magenta)" strokeWidth="1.8" fill="none" />
          <path d="M 52 230 L 65 242 L 52 270" stroke="var(--neon-magenta)" strokeWidth="1.8" fill="none" />
          <text x="26" y="184" fill="var(--neon-cyan)" fontFamily="var(--font-mono)" fontSize="7">// LIVE</text>

          {/* Right Wall: Entrance Door */}
          <polygon points="376,145 304,190 304,335 376,370" fill="#0d0d16" stroke="#2a2a35" strokeWidth="1.2" />
          <polygon points="372,150 308,193 308,330 372,365" fill="#151522" />
          <polygon points="376,140 304,185 304,188 376,143" fill="var(--neon-magenta)" />
          <line x1="340" y1="172" x2="340" y2="347" stroke="#28283c" strokeWidth="1.2" />
          <line x1="322" y1="258" x2="322" y2="276" stroke="var(--neon-cyan)" strokeWidth="2.5" strokeLinecap="round" />
          <polygon points="318,238 309,244 309,256 318,250" fill="#0a0a0f" />
          <circle cx="314" cy="245" r="1.5" fill="var(--neon-lime)" />

          {/* Center Wall: Vinyl Record Player Setup & Music Poster */}
          {/* Graphic Music Poster */}
          <rect x="165" y="148" width="70" height="52" rx="2" fill="#0d0d16" stroke="rgba(255,46,136,0.35)" strokeWidth="1" />
          <line x1="172" y1="174" x2="228" y2="174" stroke="var(--neon-magenta)" strokeWidth="1.5" />
          <path d="M 175 174 Q 185 160 195 174 T 215 174 T 225 174" fill="none" stroke="var(--neon-cyan)" strokeWidth="1.2" />
          <text x="200" y="162" fill="var(--paper)" fontFamily="var(--font-mono)" fontSize="6" letterSpacing="0.14em" textAnchor="middle">
            DARD-E-DISCO
          </text>
          <text x="200" y="190" fill="var(--neon-magenta)" fontFamily="var(--font-mono)" fontSize="5" letterSpacing="0.1em" textAnchor="middle">
            33⅓ RPM // STEREO
          </text>

          {/* Equipment Shelf */}
          <rect x="128" y="258" width="144" height="8" rx="1.5" fill="#141420" stroke="#252535" strokeWidth="1" />

          {/* Turntable base */}
          <rect x="144" y="240" width="112" height="18" rx="2" fill="#efefe8" stroke="#ccc" strokeWidth="0.5" />
          <circle cx="152" cy="249" r="2" fill="var(--neon-magenta)" />

          {/* Platter & Vinyl Record */}
          <ellipse cx="190" cy="243" rx="36" ry="12" fill="#20202c" />
          {/* Animated pulsing soundwave ring around vinyl */}
          <ellipse cx="190" cy="242" rx="42" ry="14" fill="none" stroke="var(--neon-magenta)" strokeWidth="1" strokeDasharray="3,4" opacity="0.6">
            <animate attributeName="rx" values="36;46;36" dur="3s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.7;0.2;0.7" dur="3s" repeatCount="indefinite" />
          </ellipse>

          {/* Spinning Vinyl Record Disc */}
          <ellipse cx="190" cy="242" rx="32" ry="10" fill="#0a0a0f" stroke="#333" strokeWidth="1" />
          {/* Concentric groove lines */}
          <ellipse cx="190" cy="242" rx="26" ry="8" fill="none" stroke="#1c1c28" strokeWidth="1" />
          <ellipse cx="190" cy="242" rx="20" ry="6" fill="none" stroke="#252535" strokeWidth="1" />
          {/* Magenta label */}
          <ellipse cx="190" cy="242" rx="11" ry="3.5" fill="var(--neon-magenta)" />
          {/* Spindle */}
          <circle cx="190" cy="241.5" r="1.5" fill="#ffffff" />

          {/* Tonearm */}
          <circle cx="236" cy="244" r="3" fill="#333" />
          <path d="M 236 244 L 226 236 L 205 239" fill="none" stroke="#2a2a35" strokeWidth="2.2" strokeLinecap="round" />
          <rect x="202" y="238" width="4" height="3" rx="0.5" fill="var(--neon-cyan)" />

          <text x="200" y="284" fill="var(--neon-magenta)" fontFamily="var(--font-mono)" fontSize="7" letterSpacing="0.16em" textAnchor="middle">
            EAST WALL // TURNTABLE
          </text>
        </g>
      )}

      {/* Frame border highlight */}
      <rect x="0" y="0" width="400" height="500" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2" />
    </svg>
  );
}

export default function StartScreen({ onStart, status }) {
  const [side, setSide] = useState("south");
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [privacyTab, setPrivacyTab] = useState("policy");
  const meta = SIDE_META[side] || SIDE_META.south;

  const handleStart = () => onStart(side);

  return (
    <div
      data-testid="start-screen"
      className="grain"
      style={{
        position: "fixed", inset: 0, background: "var(--paper)", color: "var(--ink)",
        display: "grid", gridTemplateColumns: "minmax(380px, 1.2fr) minmax(340px, 1fr)", zIndex: 20,
        overflow: "hidden",
      }}
    >
      {/* LEFT: content */}
      <div
        style={{
          padding: "clamp(20px, 3.5vh, 48px) clamp(24px, 4vw, 56px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflowY: "auto",
          maxHeight: "100vh",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Logo size={30} />
            <span className="font-mono" style={{ fontSize: 14, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              Dard-e-Disco
            </span>
          </div>

          <a
            href="https://www.buymeacoffee.com/aayushamann"
            target="_blank"
            rel="noopener noreferrer"
            className="bmc-button-wrap font-mono"
            aria-label="Buy me a coffee"
          >
            <span className="bmc-border-beam" aria-hidden="true" />
            <span className="bmc-button-inner">
              <span className="bmc-emoji" role="img" aria-hidden="true">☕</span>
              <span>Buy me a coffee</span>
            </span>
          </a>
        </div>

        <div style={{ maxWidth: 640, margin: "clamp(12px, 2vh, 24px) 0" }}>
          <h1
            className="font-serif"
            style={{ fontSize: "clamp(34px, 4.6vw, 76px)", lineHeight: 0.98, margin: 0, letterSpacing: "-0.02em" }}
          >
            a private club <em style={{ color: "var(--neon-magenta)" }}>for one.</em>
            <br />
            dance with <span className="neon-cyan">your shadow.</span>
          </h1>

          <p className="font-mono" style={{ marginTop: "clamp(12px, 2vh, 20px)", maxWidth: 520, fontSize: "clamp(12px, 1vw, 14px)", lineHeight: 1.6, color: "var(--ink-soft)" }}>
            Your webcam turns into a 3D soundstage. Put on headphones, shake off the desk fatigue, and watch your shadow avatar match every move.
          </p>

          {/* Standing side selector */}
          <div style={{ marginTop: "clamp(14px, 2.2vh, 24px)" }} data-testid="side-selector">
            <div className="font-mono" style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--ink-soft)", marginBottom: 8 }}>
              choose your standing side
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10, maxWidth: 460 }}>
              {SIDES.map((s) => {
                const active = side === s.id;
                return (
                  <button
                    key={s.id}
                    data-testid={`side-${s.id}`}
                    onClick={() => setSide(s.id)}
                    style={{
                      textAlign: "left",
                      padding: "10px 14px",
                      background: active ? "var(--ink)" : "transparent",
                      color: active ? "var(--paper)" : "var(--ink)",
                      border: `1.5px solid ${active ? "var(--ink)" : "rgba(10,10,15,0.25)"}`,
                      borderRadius: 10,
                      fontFamily: "var(--font-mono)",
                      cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 12,
                    }}
                  >
                    <span style={{
                      display: "inline-block", width: 10, height: 10, borderRadius: "50%",
                      background: active ? "var(--neon-cyan)" : "transparent",
                      border: `1.5px solid ${active ? "var(--neon-cyan)" : "rgba(10,10,15,0.35)"}`,
                      boxShadow: active ? "0 0 8px var(--neon-cyan)" : "none",
                      flexShrink: 0,
                    }} />
                    <span>
                      <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                        {s.label}
                      </span>
                      <br />
                      <span style={{ fontSize: 10, opacity: 0.7 }}>{s.hint}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginTop: "clamp(16px, 2.5vh, 26px)", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <button
              data-testid="enter-room-btn"
              onClick={handleStart}
              className="btn-primary pulse-ring"
              style={{
                padding: "14px 28px",
                background: "var(--ink)", color: "var(--paper)",
                border: "none",
                borderRadius: 999,
                fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13,
                letterSpacing: "0.22em", textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              enter the room →
            </button>
            <span className="font-mono" style={{ fontSize: 11, color: "var(--ink-soft)", letterSpacing: "0.08em" }}>
              requires camera · headphones recommended
            </span>
          </div>

          {status ? (
            <div className="font-mono" data-testid="start-status" style={{ marginTop: 14, fontSize: 12, color: "var(--neon-magenta)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
              status: {status}<span className="blink">_</span>
            </div>
          ) : null}
        </div>

        {/* Privacy Trust & Verification Bar */}
        <div className="privacy-trust-bar">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="privacy-pulse-dot" />
            <span
              className="font-mono"
              style={{
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                fontWeight: 700,
                color: "var(--ink)",
              }}
            >
              100% On-Device · Runs Locally
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              type="button"
              onClick={() => { setPrivacyTab("policy"); setPrivacyOpen(true); }}
              className="privacy-trigger-btn font-mono"
            >
              Privacy &amp; Security →
            </button>
          </div>
        </div>
      </div>

      {/* RIGHT: preview mockup */}
      <div style={{ position: "relative", background: "var(--ink)", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: 24, left: 24, right: 24, height: 3, background: "var(--neon-cyan)", boxShadow: "0 0 20px var(--neon-cyan)", zIndex: 4 }} />
        <div style={{ position: "absolute", bottom: 14, left: 24, right: 24, height: 2, background: "var(--neon-magenta)", boxShadow: "0 0 16px var(--neon-magenta)", zIndex: 4 }} />

        {/* Dynamic perspective room rendering */}
        <RoomPreviewSVG side={side} />

        {/* Dynamic 2D Room Radar */}
        <RoomRadar side={side} />

        {/* Vantage telemetry badge */}
        <div
          className="font-mono"
          style={{
            position: "absolute",
            bottom: "clamp(26px, 4vh, 40px)",
            left: "clamp(18px, 2.5vw, 32px)",
            right: "clamp(18px, 2.5vw, 32px)",
            color: "var(--paper)",
            zIndex: 5,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--neon-cyan)", display: "inline-block", boxShadow: "0 0 8px var(--neon-cyan)" }} />
            <span>preview // {side} view</span>
            <span style={{ opacity: 0.5 }}>—</span>
            <span style={{ color: "var(--neon-cyan)", fontSize: 10 }}>{meta.heading}</span>
          </div>
          <div style={{ fontSize: 9, opacity: 0.65, letterSpacing: "0.12em", textTransform: "uppercase" }}>
            ahead: {meta.ahead} · left: {meta.left} · right: {meta.right}
          </div>
        </div>
      </div>

      {/* Privacy & Architecture Transparency Modal */}
      <PrivacyModal
        isOpen={privacyOpen}
        onClose={() => setPrivacyOpen(false)}
        initialTab={privacyTab}
      />
    </div>
  );
}

